<template>
  <v-card>
    <v-toolbar flat>
      <v-toolbar-title>
        Roller
      </v-toolbar-title>
      <v-spacer />
      <v-menu
        v-model="addMenu"
        left
        :close-on-content-click="false"
        min-width="500"
      >
        <template #activator="{ attrs, on }">
          <v-btn
            v-bind="attrs"
            v-on="on"
            small
            :disabled="!categories.length"
            @click="onAdd"
          >
            Ny rolle
            <v-icon right> mdi-plus </v-icon>
          </v-btn>
        </template>
        <v-card>
          <v-card-title>
            Ny rolle
          </v-card-title>
          <v-card-text>
            <v-form
              ref="form"
              v-model="addForm"
            >
              <v-text-field
                v-model="addItem.title"
                label="Navn"
                autofocus
                :rules="[ v => !!v || 'Udfyld et navn' ]"
                outlined
              />
              <v-autocomplete
                v-model="addItem.categoryId"
                :items="categories"
                label="Kategori"
                item-value="id"
                item-text="title"
                :rules="[ v => !!v || 'Udfyld type' ]"
                clearable
                auto-select-first
                outlined
              />
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              text
              @click="clear"
            >
              {{ $t('common.cancel') }}
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              :disabled="!addForm"
              :loading="creating"
              @click="create"
            >
              Opret
              <v-icon right> mdi-plus </v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-menu>
    </v-toolbar>
    <v-data-table
      :items="items"
      :headers="headers"
      item-style="font-size: 10px;"
    >
      <template #[`item.allocationRequired`]="{ value }">
        <v-chip :color="value ? 'success' : 'default'" label small>
          {{ value ? 'Ja' : 'Nej' }}
        </v-chip>
      </template>
      <template #[`item.usedInSpecifications`]="{ value }">
        <v-chip :color="value ? 'success' : 'default'" label small>
          {{ value ? 'Ja' : 'Nej' }}
        </v-chip>
      </template>
      <template #[`item.defaultCostPrice`]="{ value }">
        {{ value | formatNumberFixed2 }}
      </template>
      <template #[`item.hourlyRate`]="{ value }">
        {{ value | formatNumberFixed2 }}
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex" style="gap: .5rem;">
          <v-btn
            small
            @click="onEdit(item)"
          >
            <v-icon>mdi-pencil-outline</v-icon>
          </v-btn>
          <RoleResponsibilitiesComponent :role="item" :responsibilities="responsibilities" />
          <PersonCustomerRoleComponent :role="item" />
        </div>
      </template>
    </v-data-table>

    <v-dialog
      v-model="editDialog"
      width="800"
    >
      <v-card>
        <v-card-title>
          Rediger rolle
        </v-card-title>
        <v-card-text>
          <v-form
            ref="editForm"
            v-if="editItem"
            v-model="editForm"
          >
            <v-text-field
              v-model="editItem.title"
              label="Navn"
              :rules="[v => !!v || 'Udfyld titel']"
              outlined
            />
            <v-autocomplete
              v-if="editItem.category"
              v-model="editItem.categoryId"
              :items="categories"
              label="Kategori"
              item-value="id"
              item-text="title"
              :rules="[ v => !!v || 'Udfyld type' ]"
              clearable
              auto-select-first
              outlined
            />
            <v-text-field
              v-model="editItem.minPersons"
              type="number"
              label="Min. personer"
              outlined
              clearable
            />
            <v-text-field
              v-model.number="editItem.maxPersons"
              type="number"
              label="Max. personer"
              outlined
              clearable
            />
            <v-text-field
              v-model.number="editItem.defaultCostPrice"
              type="number"
              label="Standard kostpris"
              outlined
              clearable
            />
            <v-text-field
              v-model.number="editItem.hourlyRate"
              type="number"
              label="Timepris"
              outlined
              clearable
            />
            <v-checkbox
              v-model="editItem.allocationRequired"
              label="Allokering krævet"
            />
            <v-checkbox
              v-model="editItem.usedInSpecifications"
              label="Bruges i specifikationer"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            :disabled="!editForm"
            :loading="saving"
            @click="save"
          >
            {{ $t('common.save') }}
            <v-icon right>
              mdi-content-save-outline
            </v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import RoleResponsibilitiesComponent from './RoleResponsibilitiesComponent.vue';
import PersonCustomerRoleComponent from './PersonCustomerRoleComponent.vue';

const baseUrl = 'roles';

export default {
  name: 'CustomerRoleComponent',
  components: {
    RoleResponsibilitiesComponent,
    PersonCustomerRoleComponent
  },
  props: {
    categories: {
      type: Array,
      default: () => []
    },
    responsibilities: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      creating: false,
      loading: true,
      saving: false,
      addItem: {},
      addMenu: false,
      addForm: false,
      editDialog: false,
      editItem: null,
      editForm: false,
      items: [],
      editItemRoleResponsibilities: [],
      headers: [
        { text: 'Navn', value: 'title', width: 300 },
        { text: 'Kategori', value: 'category.title' },
        { text: 'Allokering krævet', value: 'allocationRequired' },
        { text: 'Bruges i specifikationer', value: 'usedInSpecifications' },
        { text: 'Min. personer', value: 'minPersons', align: 'right' },
        { text: 'Max. personer', value: 'maxPersons', align: 'right' },
        { text: 'Standard kostpris', value: 'defaultCostPrice', align: 'right' },
        { text: 'Timepris', value: 'hourlyRate', align: 'right' },
        { text: 'Actions', value: 'actions', width: 100, align: 'right', sortable: false }
      ]
    }
  },
  mounted() {
    this.getItems();
  },
  watch: {
    editDialog(value) {
      if (!value) {
        this.editItem = null;
      }
    }
  },
  methods: {
    clear: function() {
      this.addItem = {};
      this.addMenu = false;
    },
    getItems: async function() {
      this.loading = true;

      try {
        const { data } = await this.$api.get('roles', {
          params: {
            customerId: this.$route.params.customerId
          }
        });

        this.items = data;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.loading = false;
      }
    },
    create: async function() {
      this.creating = true;

      const model = {
        ...this.addItem,
        customerId: this.$route.params.customerId
      };

      try {
        const { data } = await this.$api.post(baseUrl, model);
        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Rolle oprettet' });
        this.addMenu = false;
        this.addItem = {};
        this.getItems();
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.creating = false;
      }
    },
    save: async function() {
      this.saving = true;

      try {
        const { data } = await this.$api.patch(`roles/${this.editItem.id}`, this.editItem);

        if (!data.success) throw data.errors?.at(0)?.message ?? data.message ?? 'Something went wrong';

        this.$notifier.showSuccess({ message: 'Rolle opdateret' });
        this.getItems();
        this.editDialog = false;
      } catch (error) {
        console.error(error);
        this.$notifier.showError({});
      } finally {
        this.saving = false;
      }
    },
    onAdd: function() {
      if (this.categories?.length === 1) {        
        this.addItem.categoryId = this.categories.at(0).id;
      }
    },
    onEdit: function(item) {
      this.editItem = JSON.parse(JSON.stringify(item));
      this.editDialog = true;
    }
  }
}
</script>